/**
 * Orchestrate the per-detail-level capture of an element into a
 * `CapturedContext`. Compact omits expensive fields (computed styles,
 * nearby DOM); forensic emits everything.
 */
import { getAccessibility } from "./accessibility.js";
import { getBoundingBox, isPositionFixed } from "./boundingBox.js";
import { getComputedStylesString } from "./computedStyles.js";
import { getCssSelector, getXPath, stableClasses } from "./elementPath.js";
import { getNearbyElements, getNearbyText } from "./nearbyText.js";
import { getReactComponents } from "./reactFiber.js";
const truncate = (s, n) => s.length > n ? `${s.slice(0, n - 1)}…` : s;
const urlOf = (el) => {
    const view = el.ownerDocument?.defaultView ?? window;
    try {
        return view?.location?.href;
    }
    catch {
        return undefined;
    }
};
export const captureAfsContext = (el, options) => {
    const detail = options.detail ?? "standard";
    const cssClasses = stableClasses(el).join(" ");
    const base = {
        element: el.tagName.toLowerCase(),
        elementPath: getCssSelector(el),
        anchor: options.anchor,
        ...(urlOf(el) ? { url: urlOf(el) } : {}),
        ...(cssClasses ? { cssClasses } : {}),
    };
    if (detail === "compact") {
        return base;
    }
    const boundingBox = getBoundingBox(el);
    const accessibility = getAccessibility(el);
    if (detail === "standard") {
        return {
            ...base,
            boundingBox,
            ...(accessibility ? { accessibility } : {}),
            ...(options.selectedText ? { selectedText: options.selectedText } : {}),
            reactComponents: getReactComponents(el, "standard"),
        };
    }
    // detailed + forensic
    const nearbyText = truncate(getNearbyText(el), 240);
    const computedStyles = getComputedStylesString(el, detail);
    const reactComponents = getReactComponents(el, detail);
    const fullPath = getXPath(el);
    if (detail === "detailed") {
        return {
            ...base,
            boundingBox,
            ...(accessibility ? { accessibility } : {}),
            ...(nearbyText ? { nearbyText } : {}),
            ...(reactComponents ? { reactComponents } : {}),
            ...(computedStyles ? { computedStyles } : {}),
            ...(options.selectedText ? { selectedText: options.selectedText } : {}),
            isFixed: isPositionFixed(el),
            fullPath,
        };
    }
    // forensic
    return {
        ...base,
        boundingBox,
        ...(accessibility ? { accessibility } : {}),
        ...(nearbyText ? { nearbyText } : {}),
        ...(reactComponents ? { reactComponents } : {}),
        ...(computedStyles ? { computedStyles } : {}),
        ...(options.selectedText ? { selectedText: options.selectedText } : {}),
        nearbyElements: getNearbyElements(el),
        isFixed: isPositionFixed(el),
        fullPath,
    };
};
//# sourceMappingURL=captureContext.js.map